<template>
  <div
    ref="fullHero"
    v-editable="blok"
    class="full-hero"
    :class="[
      animate && 'full-hero--animate',
      blok.small_image &&
        blok.small_image.filename &&
        'full-hero--has-thumbnail',
    ]"
  >
    <div class="full-hero__content">
      <p v-if="blok.eyebrow" class="eyebrow" role="doc-subtitle">
        {{ blok.eyebrow }}
      </p>
      <CustomHeading
        :blok="blok.heading[0]"
        :class="blok.extend_heading_container && 'custom-heading--wide'"
      />
      <div class="full-hero__text-cta">
        <RichTextEditor :blok="{ text: blok.text }" />
        <CTAGroup v-if="blok.ctas" :ctas="blok.ctas" />
        <RichTextEditor
          v-if="blok.legalText"
          class="mt-4"
          :blok="{ text: blok.legalText }"
        />
      </div>
    </div>
    <div class="full-hero__image-container">
      <div class="full-hero__image">
        <Picture
          v-editable="blok"
          class="full-hero__image-large"
          loading="eager"
          :width="1320"
          :height="742"
          :blok="makePicture(blok.large_image)"
        />
      </div>
      <Picture
        v-if="blok.small_image && blok.small_image.filename"
        v-editable="blok"
        class="full-hero__image-small"
        loading="eager"
        :width="smallImageWidth"
        :height="320"
        :blok="makePicture(blok.small_image)"
        :style="`--small-image-width: ${smallImageWidth}; position: absolute`"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { FullHeroBlokProps, ImageBlokProps } from '@/types';

// Plugins
const { $noMotionPreference } = useNuxtApp();

// Props
defineProps<{
  blok: FullHeroBlokProps;
}>();

// Images
const smallImageWidth = 240;
const makePicture = (src: ImageBlokProps) => ({
  image: src,
});

// Variables & refs
let observer: IntersectionObserver;
const animate = ref(false);
const fullHero = ref<HTMLDivElement>();

// Lifecycle : onMounted
onMounted(() => {
  // Only set up IO if no motion preference and there's an item to animate
  if ($noMotionPreference === true && fullHero.value) {
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting === true) {
          animate.value = true;
        }
      });
    });
    observer.observe(fullHero.value);
  }
});

// Lifecycle : onBeforeUnmount
onBeforeUnmount(() => {
  if (observer) observer.disconnect();
});
</script>

<style lang="scss">
.full-hero {
  @include container;

  padding-block-start: var(--spacing-xl);

  @media all and #{$mq-s-max} {
    &--has-thumbnail {
      padding-bottom: calc(var(--spacing-xxl) + var(--spacing-xl));
    }
  }

  .custom-heading {
    @media all and #{$mq-m-max} {
      margin-block-end: var(--spacing-xs);
    }

    @media all and #{$mq-m-min} {
      width: columns(10);
    }

    &--wide {
      @media all and #{$mq-m-min} {
        width: columns(13);
      }
    }
  }

  &__content {
    margin-block-end: var(--spacing-xxl);

    @media all and #{$mq-s-max} {
      max-width: columns(12);
    }

    /* Linting error is false positive */
    @media all and (min-width: #{$mq-m-min-value}) and (max-width: #{$mq-m-max-value}) {
      max-width: columns(16);
    }

    @media all and #{$mq-l-min} {
      column-gap: columns(1);
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
    }

    > .eyebrow {
      margin-block-end: var(--spacing-xs);

      @media all and #{$mq-l-min} {
        flex-basis: 100%;
      }
    }
  }

  &__text-cta {
    margin-inline-end: columns(1);
    position: relative;
    width: 100%;
    z-index: 2;

    @media all and #{$mq-l-min} {
      max-width: columns(7);
    }
  }

  .cta-group {
    margin-block-start: var(--spacing-s);
  }

  &__image-container {
    position: relative;
  }

  &__image {
    &-small {
      inset-inline-end: columns(2);
      max-width: calc(var(--small-image-width) * 1px);
      min-width: 116px;
      position: absolute;
      z-index: 1;

      @media all and #{$mq-s-max} {
        inset-block-end: calc(var(--spacing-xl) * -1);
        max-width: 180px;
        width: columns(5);
      }

      @media all and #{$mq-m-min} {
        inset-block-start: calc(var(--spacing-m) * -1);
        width: columns(4);
      }
    }

    &-large {
      width: 100%;

      img {
        display: block;
        height: 100%;
        max-height: 742px;
        min-height: 280px;
        object-fit: cover;
        width: 100%;
      }
    }
  }
}

@media screen and (prefers-reduced-motion: no-preference) {
  .full-hero {
    opacity: 0;
  }

  .full-hero--animate {
    --timing-text-cta-fade-delay: calc(
      var(--timing-heading-wipe) + var(--timing-hero-start-delay)
    );
    --timing-large-image-delay: calc(
      var(--timing-text-cta-fade-delay) + var(--timing-text-cta-fade)
    );
    --timing-small-image-delay: calc(
      var(--timing-large-image-delay) + var(--timing-large-image-fade)
    );

    opacity: 1;
    transition: opacity 0ms linear var(--timing-hero-start-delay);

    .eyebrow {
      animation: fade-in var(--timing-eyebrow) var(--easing-3) forwards;
      animation-delay: var(--timing-hero-start-delay);
    }

    .custom-heading {
      animation: wipe-left var(--timing-heading-wipe) var(--easing-2),
        scale var(--timing-heading-scale) var(--easing-2),
        fade-in var(--timing-heading-fade) var(--easing-3);
      animation-delay: var(--timing-hero-start-delay);
      animation-fill-mode: forwards;
      will-change: clip-path, transform, opacity;
    }

    .full-hero__text-cta {
      animation: fade-in var(--timing-text-cta-fade) var(--easing-3)
        var(--timing-text-cta-fade-delay) forwards;
      opacity: 0;
      will-change: opacity;
    }

    .full-hero__image {
      animation: wipe-right var(--timing-large-image-wipe) var(--easing-2)
        var(--timing-large-image-delay) forwards;
      border-start-end-radius: 24px;
      overflow: hidden;
      will-change: clip-path;

      img {
        @media all and #{$mq-m-max} {
          aspect-ratio: 1;
          object-fit: cover;
        }
      }

      &-small {
        animation: wipe-left var(--timing-small-image-wipe) var(--easing-2),
          fade-in var(--timing-small-image-fade) var(--easing-3);
        animation-delay: var(--timing-small-image-delay);
        animation-fill-mode: forwards;
        opacity: 0;
        will-change: clip-path, opacity;
      }

      &-large {
        animation: scale var(--timing-large-image-scale) var(--easing-2),
          fade-in var(--timing-large-image-fade) var(--easing-3);
        animation-delay: var(--timing-large-image-delay);
        animation-fill-mode: forwards;
        opacity: 0;
        transform: scale(1.1);
        will-change: transform, opacity, clip-path;
      }
    }
  }
}
</style>
